
import React, { useState, useEffect } from 'react';
import { Calendar, Upload, UserPlus, Plus, Users, Clock, ChevronRight } from 'lucide-react';
import ClockComponent from '@/components/custom/utilities/clock';
import BackendTemplateConfig from '@/configs/BackendTemplateConfig';

const Dashboard = () => {
  const [userName] = useState('Manuela');
  const [greeting, setGreeting] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const config = BackendTemplateConfig;

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 17) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  // Sample upcoming meetings data
  const upcomingMeetings = [
    { id: 1, title: 'Name of Person', time: '10:00 AM', duration: '30 min', attendees: 2 },
    { id: 2, title: 'Name of Person', time: '2:00 PM', duration: '1 hour', attendees: 2 },
    { id: 3, title: 'Workshop', time: '4:30 PM', duration: '45 min', attendees: 6 },
  ];

  const actionCards = [
    {
      id: 'upload',
      title: 'Upload File',
      icon: Upload,
      color: config.colors.primary,
      description: 'Upload and manage your documents'
    },
    {
      id: 'user',
      title: 'Add User',
      icon: UserPlus,
      color: config.colors.success,
      description: 'Invite team members to collaborate'
    },
    {
      id: 'meeting',
      title: 'Add Meeting',
      icon: Calendar,
      color: config.colors.warning,
      description: 'Schedule a new meeting'
    },
    {
      id: 'workshop',
      title: 'Create Workshop',
      icon: Users,
      color: config.colors.danger,
      description: 'Organize a collaborative session'
    }
  ];

  const handleAction = (actionId) => {
    console.log(`Action triggered: ${actionId}`);
  };

  return (
    <div className={`${config.container.minHeight} ${config.container.backgroundGradient} ${config.container.paddingLarge}`}>
      <div className={`${config.container.maxWidth} ${config.container.marginX}`}>
        
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className={`${config.typography.pageTitle} mb-2`}>
            {greeting}, <span className={config.colors.primary.text}>{userName}</span>! 👋
          </h1>
          <p className={config.typography.body}>Welcome back to your dashboard</p>
        </div>

        {/* Clock and Meetings Section */}
        <div className={`mb-8 ${config.layout.flex} ${config.spacing.lg}`}>
          
          {/* Clock Section - 1/4 width */}
          <div className="w-1/4">
            <ClockComponent />
          </div>

          {/* Upcoming Meetings Section - 3/4 width */}
          <div className={`w-3/4 ${config.card.base} ${config.card.paddingLarge}`}>
            <div className={`${config.layout.flexStart} mb-4`}>
              <Clock className={`${config.icons.large} ${config.colors.primary.text} mr-2`} />
              <h2 className={config.typography.sectionTitle}>Upcoming Meetings</h2>
            </div>
            
            <div className={config.card.spacingLarge}>
              {upcomingMeetings.map((meeting, index) => (
                <div
                  key={meeting.id}
                  className={`${config.layout.flexBetween} ${config.card.padding} ${config.colors.neutral[50]} ${config.borderRadius.lg} ${config.borders.default} ${config.colors.neutral.border.light} ${config.animations.hover.shadow} ${config.animations.transition.all} ${config.animations.duration.normal} cursor-pointer hover:scale-[1.02]`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`${config.layout.flex} items-center ${config.spacing.md}`}>
                    <div className={`w-12 h-12 ${config.colors.primary[100]} ${config.borderRadius.full} ${config.layout.flexCenter}`}>
                      <Calendar className={`${config.icons.normal} ${config.colors.primary.text}`} />
                    </div>
                    <div>
                      <h3 className={config.typography.heading}>{meeting.title}</h3>
                      <p className={config.typography.small}>{meeting.time} • {meeting.duration}</p>
                    </div>
                  </div>
                  
                  <div className={`${config.layout.flex} items-center ${config.spacing.sm}`}>
                    <div className="flex -space-x-2">
                      {[...Array(Math.min(3, meeting.attendees))].map((_, i) => (
                        <div
                          key={i}
                          className={`w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 ${config.borderRadius.full} border-2 ${config.colors.white} ${config.layout.flexCenter} text-white text-xs font-semibold`}
                        >
                          {String.fromCharCode(65 + i)}
                        </div>
                      ))}
                      {meeting.attendees > 3 && (
                        <div className={`w-8 h-8 ${config.colors.neutral[200]} ${config.borderRadius.full} border-2 ${config.colors.white} ${config.layout.flexCenter} ${config.typography.small} font-semibold`}>
                          +{meeting.attendees - 3}
                        </div>
                      )}
                    </div>
                    <ChevronRight className={`${config.icons.normal} ${config.colors.neutral.text.light}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Cards Section */}
        <div className="mb-8">
          <h2 className={`${config.typography.sectionTitle} mb-4`}>Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {actionCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className={`relative group cursor-pointer transform ${config.animations.transition.all} ${config.animations.duration.normal} ${config.animations.hover.scale}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onMouseEnter={() => setHoveredCard(card.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => handleAction(card.id)}
                >
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 ${config.animations.transition.opacity} ${config.animations.duration.normal} ${config.borderRadius.xl} blur-xl ${card.color[500]}`} />
                  
                  <div className={`relative ${config.card.base} ${config.card.paddingLarge} ${config.animations.hover.shadow} ${config.animations.transition.all} ${config.animations.duration.normal}`}>
                    <div className={`w-14 h-14 ${card.color[500]} ${config.borderRadius.lg} ${config.layout.flexCenter} mb-4 group-hover:scale-110 ${config.animations.transition.transform} ${config.animations.duration.normal}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className={`${config.typography.heading} mb-2`}>{card.title}</h3>
                    <p className={`${config.typography.body} mb-4`}>{card.description}</p>
                    <div className={`${config.layout.flex} items-center ${config.colors.primary.text} font-semibold group-hover:translate-x-2 ${config.animations.transition.transform} ${config.animations.duration.normal}`}>
                      <span className="text-sm">Get Started</span>
                      <ChevronRight className={`${config.icons.medium} ml-1`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className={`bg-gradient-to-r ${config.colors.primary[500]} ${config.colors.primary[600]} ${config.borderRadius.xl} ${config.card.paddingLarge} text-white`}>
            <div className="text-3xl font-bold mb-2">24</div>
            <div className="text-blue-100">Active Projects</div>
          </div>
          <div className={`bg-gradient-to-r ${config.colors.success[500]} ${config.colors.success[600]} ${config.borderRadius.xl} ${config.card.paddingLarge} text-white`}>
            <div className="text-3xl font-bold mb-2">87%</div>
            <div className="text-green-100">Tasks Completed</div>
          </div>
          <div className={`bg-gradient-to-r from-purple-500 to-purple-600 ${config.borderRadius.xl} ${config.card.paddingLarge} text-white`}>
            <div className="text-3xl font-bold mb-2">142</div>
            <div className="text-purple-100">Team Members</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;